import { ProductSlug } from "@api";

/**
 * Every card image is authored to render at CARD_IMAGE_HEIGHT px tall, so a card that shows
 * two colorways lines them up and every card in a row shares the same image band. Widths are
 * the source aspect ratio scaled to that height; the flex row shrinks both images by the same
 * factor when a column gets narrow, which keeps the pair matched at any breakpoint.
 */
const CARD_IMAGE_HEIGHT = 205;

interface CardImage {
  src: string;
  width: number;
  height: number;
}

interface CardMouthpiece {
  label: string;
  imgName: string;
  imgWidth: number;
  imgHeight: number;
}

interface AdditionalProductParts {
  badge?: string;
  capacity?: string;
  description?: string;
  images?: CardImage[];
  mouthpieces?: CardMouthpiece[];
}

const image = (src: string, sourceWidth: number, sourceHeight: number): CardImage => ({
  src,
  width: Math.round((CARD_IMAGE_HEIGHT * sourceWidth) / sourceHeight),
  height: CARD_IMAGE_HEIGHT,
});

const mouthpieces: CardMouthpiece[] = [
  {
    label: "Ceramic Barrel Black",
    imgName: "/images/products/@global/mouthpieces/ceramic-barrel-black.png",
    imgWidth: 35,
    imgHeight: 95,
  },
  {
    label: "Ceramic<br/>Flat White",
    imgName: "/images/products/@global/mouthpieces/ceramic-barrel-white.png",
    imgWidth: 35,
    imgHeight: 95,
  },
  {
    label: "Hemp<br/>Flat Natural",
    imgName: "/images/products/@global/mouthpieces/hemp-flat-natural.png",
    imgWidth: 35,
    imgHeight: 95,
  },
];

/** EZ Click Glass Classic is stocked with the three Plastic Flat options and nothing else. */
const plasticFlatMouthpieces: CardMouthpiece[] = [
  {
    label: "Plastic<br/>Flat Clear",
    imgName: "/images/products/@global/mouthpieces/plastic-flat-clear.png",
    imgWidth: 35,
    imgHeight: 95,
  },
  {
    label: "Plastic<br/>Flat Black",
    imgName: "/images/products/@global/mouthpieces/plastic-flat-black.png",
    imgWidth: 35,
    imgHeight: 95,
  },
  {
    label: "Plastic<br/>Flat White",
    imgName: "/images/products/@global/mouthpieces/plastic-flat-white.png",
    imgWidth: 35,
    imgHeight: 95,
  },
];

export const additionalProductParts = (slug: string): AdditionalProductParts => {
  switch (slug) {
    case ProductSlug.AxilMiniAir:
      return {
        capacity: "0.5mL",
        description: "Delightfully different. Small on the outside, big on the inside. Engineered for your distillates and novel cannabinoids, and compatible with liquid live resin.",
        images: [
          image("/images/products/all-in-ones/active-axil-mini-air/options.1.png", 664, 2568),
          image("/images/products/all-in-ones/active-axil-mini-air/options.2.png", 668, 2576),
        ],
      };
    // Mini Pro's CMS thumb is already the white render, so no override is needed here.
    case ProductSlug.AxilMiniPro:
      return {
        capacity: "0.5mL",
      };
    case ProductSlug.Axil:
      return {
        capacity: "1.0mL and 2.0mL",
        // Copy of options.2.2.png under a single-dot name: the R2 objects for this folder's
        // options.* keys no longer match the local sources (options.2 holds the white render,
        // options.2.2 holds the black one), so the white Axil needs its own clean key.
        images: [image("/images/products/all-in-ones/active-axil/product-thumb-white.png", 313, 510)],
      };
    case ProductSlug.Leaf:
      return {
        capacity: "1.0mL and 2.0mL",
        description: "Delightfully different. Small on the outside, big on the inside. Engineered for your distillates and novel cannabinoids, and compatible with liquid live resin.",
        images: [image("/images/products/all-in-ones/active-leaf/options.2.png", 209, 340)],
      };
    case ProductSlug.Ceramic:
      return {
        capacity: "0.5mL",
        description: "Engineered for your premium extracts. All the looks and outstanding flavor delivery with a glass body and ceramic zirconia centerpost.",
        mouthpieces,
      };
    case ProductSlug.EzClickCeramicPro:
      return {
        badge: "Pro",
        capacity: "0.5mL and 1.0mL",
        description: "Engineered for premium extracts, the Ceramic Pro features a glass body and zirconia center post for exceptional flavor delivery.",
        mouthpieces,
      };
    // CMS thumbPath points at products/cartridges/active-ezclick-glass/, a directory that does
    // not exist — the real renders live under ezclick-glass/, matching the permalink.
    case ProductSlug.EzClickGlass:
      return {
        capacity: "0.5mL and 1.0mL",
        description: "A balance of taste, affordability, and reliability for your distillates and novel cannabinoids in a glass body.",
        images: [image("/images/products/cartridges/ezclick-glass/product-thumb.png", 300, 440)],
        mouthpieces: plasticFlatMouthpieces,
      };
    // Batteries carry no fill capacity, and the CMS thumbPath for both is missing or points at
    // the wrong directory, so the image is named here instead of coming off the product.
    case ProductSlug.RootXL:
      return {
        images: [image("/images/products/batteries/active-root-xl/product-thumb.png", 300, 440)],
      };
    case ProductSlug.ActiveSprout:
      return {
        images: [image("/images/products/batteries/active-sprout/product-thumb.png", 300, 440)],
      };
    default:
      return {};
  }
};
